/**
 * PageRenderer - Canonical Page Builder Renderer with Data Fetching
 * 
 * Renders pages through the canonical Page Builder path with REAL content data:
 * Route → CanonicalRouteProvider → registry → PageRenderer → API-hydrated sections
 * 
 * Features:
 * - Fetches page definition from Page Builder API
 * - Fetches hydrated data for each content section
 * - Loading/empty/error states per section
 * - Admin preview mode with ?preview=draft
 * - Canonical verification logging
 * - Series section rendering support
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useSearchParams, Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, Eye, ChevronRight, TrendingUp, Star, Clock, Users, Heart, Image as ImageIcon } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Import admin sections
import { AdminPagesListSection } from '../admin/page_builder';

// Import series section renderers
import {
  SeriesHeroSection,
  EpisodeListSection,
  SeriesInfoPanelSection,
  SeriesCommentsSection,
  RelatedSeriesSection,
  SeriesCreatorSpotlightSection,
  SeriesCTASection,
  SERIES_SECTION_COMPONENTS,
} from './page-builder/series';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Series section component mapping
const SERIES_RENDERERS = {
  section_series_hero: SeriesHeroSection,
  section_episode_list: EpisodeListSection,
  section_series_info: SeriesInfoPanelSection,
  section_series_comments: SeriesCommentsSection,
  section_related_series: RelatedSeriesSection,
  section_series_creator: SeriesCreatorSpotlightSection,
  section_series_cta: SeriesCTASection,
};

// Check if user is admin
const useIsAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setIsAdmin(user?.role === 'admin' || user?.is_admin);
      } catch (e) {
        setIsAdmin(false);
      }
    }
  }, []);
  
  return isAdmin;
};

/**
 * ContentCard - Reusable card component for series/novels
 */
const ContentCard = ({ item, variant = 'discovery' }) => {
  const coverUrl = item.cover_url || item.cover_image_url || item.thumbnail_url;
  
  return (
    <Link 
      to={`/series/${item.id}`}
      className="group block"
    >
      <div className="relative aspect-[3/4] bg-zinc-800 rounded-lg overflow-hidden mb-2">
        {coverUrl ? (
          <img 
            src={coverUrl} 
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-zinc-600" />
          </div>
        )}
        {variant === 'featured' && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-orange-500 text-white text-xs">Featured</Badge>
          </div>
        )}
      </div>
      <h3 className="text-white font-medium text-sm truncate group-hover:text-orange-400 transition-colors">
        {item.title}
      </h3>
      <p className="text-zinc-500 text-xs truncate">
        {item.creator_name || item.author_name || 'Creator'}
      </p>
      {item.metrics_cache && (
        <div className="flex items-center gap-2 mt-1 text-zinc-600 text-xs">
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {item.metrics_cache.views?.toLocaleString() || 0}
          </span>
          <span className="flex items-center gap-1">
            <Heart className="w-3 h-3" />
            {item.metrics_cache.likes?.toLocaleString() || 0}
          </span>
        </div>
      )}
    </Link>
  );
};

/**
 * CreatorCard - Card for displaying creators
 */
const CreatorCard = ({ creator, showFollow = true }) => {
  return (
    <Link 
      to={`/u/${creator.handle || creator.user_id}`}
      className="group p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors text-center"
    >
      <div className="w-16 h-16 rounded-full bg-zinc-700 mx-auto mb-3 overflow-hidden">
        {creator.avatar_url ? (
          <img src={creator.avatar_url} alt={creator.display_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xl font-bold">
            {(creator.display_name || 'C')[0].toUpperCase()}
          </div>
        )}
      </div>
      <h3 className="text-white font-medium text-sm truncate group-hover:text-orange-400">
        {creator.display_name}
      </h3>
      <p className="text-zinc-500 text-xs">@{creator.handle || 'creator'}</p>
      <div className="flex justify-center gap-3 mt-2 text-xs text-zinc-500">
        <span>{creator.follower_count?.toLocaleString() || 0} followers</span>
        <span>{creator.works_count || 0} works</span>
      </div>
    </Link>
  );
};

/**
 * ArtworkCard - Card for gallery artwork
 */
const ArtworkCard = ({ item }) => {
  return (
    <Link 
      to={`/art/${item.id}`}
      className="group block"
    >
      <div className="relative aspect-square bg-zinc-800 rounded-lg overflow-hidden mb-2">
        {item.image_url || item.thumbnail_url ? (
          <img 
            src={item.thumbnail_url || item.image_url} 
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-zinc-600" />
          </div>
        )}
      </div>
      <h3 className="text-white font-medium text-sm truncate">{item.title}</h3>
      <p className="text-zinc-500 text-xs truncate">{item.creator_name}</p>
    </Link>
  );
};

/**
 * SectionLoading - Loading state for a section
 */
const SectionLoading = ({ heading }) => (
  <section className="py-8">
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <h2 className="text-xl font-semibold text-white mb-6">{heading}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-[3/4] bg-zinc-800 rounded-lg animate-pulse" />
        ))}
      </div>
    </div>
  </section>
);

/**
 * SectionEmpty - Empty state for a section
 */
const SectionEmpty = ({ heading, message }) => (
  <section className="py-8">
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <h2 className="text-xl font-semibold text-white mb-6">{heading}</h2>
      <div className="text-center py-12 bg-zinc-800/30 rounded-lg">
        <p className="text-zinc-500">{message || 'No content available yet'}</p>
      </div>
    </div>
  </section>
);

/**
 * SectionError - Error state for a section
 */
const SectionError = ({ heading, error }) => (
  <section className="py-8">
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <h2 className="text-xl font-semibold text-white mb-6">{heading}</h2>
      <div className="text-center py-8 bg-red-500/10 border border-red-500/20 rounded-lg">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-400 text-sm">Failed to load content</p>
      </div>
    </div>
  </section>
);

/**
 * DataFetchingSection - Section with automatic data fetching
 */
const DataFetchingSection = ({ block, isPreview = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const type = block._templateId || block.type;
  const props = block.props || {};
  
  // Determine which section types need data fetching
  const needsData = [
    'trending_rail', 'featured_grid', 'new_releases_rail', 
    'community_picks', 'creator_grid', 'creator_spotlight',
    'leaderboard_strip', 'art_gallery_grid', 'genre_spotlight'
  ].includes(type);
  
  useEffect(() => {
    if (!needsData) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: props.maxItems || 12,
          content_type: props.contentType || 'all',
        });
        
        const response = await axios.get(
          `${API}/api/page-builder/section-data/${type}?${params}`
        );
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error(`[Section] Error fetching ${type}:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [type, needsData, props.maxItems, props.contentType]);
  
  // Build background styles
  const background = block.background || {};
  let bgStyle = {};
  if (background.type === 'solid' && background.config?.color) {
    bgStyle.backgroundColor = background.config.color;
  } else if (background.type === 'gradient' && background.config) {
    const { direction, from, to } = background.config;
    const dir = direction || 'to-bottom';
    bgStyle.background = `linear-gradient(${dir.replace('to-', 'to ')}, ${from}, ${to})`;
  } else if (background.type === 'image' && background.config?.url) {
    bgStyle.backgroundImage = `url(${background.config.url})`;
    bgStyle.backgroundSize = background.config.size || 'cover';
    bgStyle.backgroundPosition = background.config.position || 'center';
  }
  
  const heading = props.heading || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  // Loading state
  if (loading && needsData) {
    return <SectionLoading heading={heading} />;
  }
  
  // Error state
  if (error && needsData) {
    return <SectionError heading={heading} error={error} />;
  }
  
  // Render based on section type
  switch (type) {
    // Admin sections
    case 'admin_pages_list':
      return <AdminPagesListSection settings={props} isPreview={isPreview} />;
    
    // Hero sections (no data fetching needed)
    case 'hero_overlay':
      return (
        <section className="relative py-16 md:py-24" style={bgStyle}>
          {background.type === 'image' && background.overlayOpacity > 0 && (
            <div className="absolute inset-0 bg-black" style={{ opacity: background.overlayOpacity / 100 }} />
          )}
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            {props.showBadge && props.badge && (
              <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/30">
                {props.badge}
              </Badge>
            )}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {props.heading || 'Hero Heading'}
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto mb-8">
              {props.subheading}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {props.ctaPrimaryLabel && (
                <Link 
                  to={props.ctaPrimaryUrl || '#'} 
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
                >
                  {props.ctaPrimaryLabel}
                </Link>
              )}
              {props.ctaSecondaryLabel && (
                <Link 
                  to={props.ctaSecondaryUrl || '#'} 
                  className="px-6 py-3 border border-zinc-600 hover:border-zinc-500 text-white font-medium rounded-lg transition-colors"
                >
                  {props.ctaSecondaryLabel}
                </Link>
              )}
            </div>
          </div>
        </section>
      );
    
    case 'hero_minimal':
      return (
        <section className="py-8" style={bgStyle}>
          <div className={`max-w-7xl mx-auto px-4 md:px-8 ${props.alignment === 'center' ? 'text-center' : ''}`}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {props.heading || 'Page Title'}
            </h1>
            {props.subheading && <p className="text-lg text-zinc-400">{props.subheading}</p>}
            {props.showDivider && <div className="mt-6 border-b border-zinc-800" />}
          </div>
        </section>
      );
    
    case 'hero_split':
      return (
        <section className="py-12" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{props.heading}</h1>
                <p className="text-lg text-zinc-400 mb-6">{props.subheading}</p>
                {props.ctaLabel && (
                  <Link to={props.ctaUrl || '#'} className="inline-block px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg">
                    {props.ctaLabel}
                  </Link>
                )}
              </div>
              <div className="bg-zinc-800 rounded-lg aspect-video flex items-center justify-center">
                {props.mediaUrl ? (
                  <img src={props.mediaUrl} alt={props.mediaAlt || ''} className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <span className="text-zinc-500">Media placeholder</span>
                )}
              </div>
            </div>
          </div>
        </section>
      );
    
    // Content sections with data
    case 'trending_rail':
    case 'new_releases_rail':
    case 'community_picks':
    case 'genre_spotlight': {
      const items = data?.items || [];
      
      if (items.length === 0) {
        return <SectionEmpty heading={heading} message="No content available yet. Check back soon!" />;
      }
      
      return (
        <section className="py-8" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                {type === 'trending_rail' && <TrendingUp className="w-5 h-5 text-orange-500" />}
                {type === 'new_releases_rail' && <Clock className="w-5 h-5 text-orange-500" />}
                {type === 'community_picks' && <Star className="w-5 h-5 text-orange-500" />}
                <h2 className="text-xl font-semibold text-white">{heading}</h2>
              </div>
              <Link to="/discover" className="text-orange-400 hover:text-orange-300 text-sm flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {items.slice(0, props.maxItems || 12).map((item, index) => (
                <ContentCard key={item.id || index} item={item} variant="discovery" />
              ))}
            </div>
          </div>
        </section>
      );
    }
    
    case 'featured_grid': {
      const items = data?.items || [];
      
      if (items.length === 0) {
        return <SectionEmpty heading={heading} message="No featured content yet." />;
      }
      
      return (
        <section className="py-8" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-orange-500" />
                <h2 className="text-xl font-semibold text-white">{heading}</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.slice(0, props.maxItems || 6).map((item, index) => (
                <ContentCard key={item.id || index} item={item} variant="featured" />
              ))}
            </div>
          </div>
        </section>
      );
    }
    
    case 'creator_grid':
    case 'leaderboard_strip': {
      const creators = data?.items || [];
      
      if (creators.length === 0) {
        return <SectionEmpty heading={heading} message="No creators to display yet." />;
      }
      
      return (
        <section className="py-8" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                <h2 className="text-xl font-semibold text-white">{heading}</h2>
              </div>
              <Link to="/leaderboard" className="text-orange-400 hover:text-orange-300 text-sm flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {creators.slice(0, props.maxItems || 8).map((creator, index) => (
                <CreatorCard key={creator.user_id || index} creator={creator} />
              ))}
            </div>
          </div>
        </section>
      );
    }
    
    case 'creator_spotlight': {
      const creator = data?.creator;
      const works = data?.works || [];
      
      if (!creator) {
        return <SectionEmpty heading={heading} message="No featured creator at the moment." />;
      }
      
      return (
        <section className="py-8" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-xl font-semibold text-white mb-6">{heading}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1 p-6 bg-zinc-800/50 rounded-lg text-center">
                <div className="w-24 h-24 rounded-full bg-zinc-700 mx-auto mb-4 overflow-hidden">
                  {creator.avatar_url ? (
                    <img src={creator.avatar_url} alt={creator.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500 text-2xl font-bold">
                      {(creator.display_name || 'C')[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-white">{creator.display_name}</h3>
                <p className="text-zinc-400 text-sm">@{creator.handle}</p>
                <p className="text-zinc-500 text-sm mt-2 line-clamp-3">{creator.bio}</p>
                <div className="flex justify-center gap-4 mt-4 text-xs text-zinc-500">
                  <span>{creator.follower_count?.toLocaleString() || 0} followers</span>
                  <span>{creator.works_count || 0} works</span>
                </div>
                <Link 
                  to={`/u/${creator.handle || creator.user_id}`}
                  className="mt-4 inline-block px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg"
                >
                  View Profile
                </Link>
              </div>
              <div className="md:col-span-2">
                <h4 className="text-white font-medium mb-4">Latest Works</h4>
                {works.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {works.map((work, index) => (
                      <ContentCard key={work.id || index} item={work} />
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm">No works published yet.</p>
                )}
              </div>
            </div>
          </div>
        </section>
      );
    }
    
    case 'art_gallery_grid': {
      const artworks = data?.items || [];
      
      if (artworks.length === 0) {
        return <SectionEmpty heading={heading} message="No artwork in the gallery yet." />;
      }
      
      return (
        <section className="py-8" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">{heading}</h2>
              <Link to="/gallery" className="text-orange-400 hover:text-orange-300 text-sm flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {artworks.slice(0, props.maxItems || 12).map((item, index) => (
                <ArtworkCard key={item.id || index} item={item} />
              ))}
            </div>
          </div>
        </section>
      );
    }
    
    // CTA sections
    case 'cta_banner':
      return (
        <section 
          className="py-8"
          style={{ 
            background: background.type === 'gradient' 
              ? `linear-gradient(to right, ${background.config?.from || '#f97316'}, ${background.config?.to || '#ea580c'})`
              : background.config?.color || '#f97316'
          }}
        >
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">{props.heading}</h2>
            <p className="text-white/80 mb-4">{props.subheading}</p>
            {props.ctaLabel && (
              <Link 
                to={props.ctaUrl || '#'} 
                className="inline-block px-6 py-3 bg-white text-orange-600 font-medium rounded-lg hover:bg-zinc-100"
              >
                {props.ctaLabel}
              </Link>
            )}
          </div>
        </section>
      );
    
    case 'transparency_footer':
      return (
        <section className="py-12 bg-black">
          <div className="max-w-4xl mx-auto px-4 text-center">
            {props.showHeading && (
              <h2 className="text-xl font-semibold text-white mb-8">{props.heading}</h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {(props.items || []).map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-zinc-800 rounded-full mx-auto mb-3 flex items-center justify-center">
                    <span className="text-orange-500 text-xl">♦</span>
                  </div>
                  <h3 className="text-white font-medium mb-1">{item.label}</h3>
                  <p className="text-zinc-500 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
            {props.showFooterText && (
              <p className="text-zinc-600 text-sm">{props.footerText}</p>
            )}
          </div>
        </section>
      );
    
    case 'announcement_banner':
      return (
        <section className="py-3 bg-blue-600 text-white text-center">
          <p className="text-sm">
            {props.message}
            {props.showLink && props.linkLabel && (
              <Link to={props.linkUrl || '#'} className="ml-2 underline">
                {props.linkLabel}
              </Link>
            )}
          </p>
        </section>
      );
    
    case 'category_spotlight':
    case 'creator_cta':
      return (
        <section className="py-12" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{props.heading}</h2>
                <p className="text-zinc-400 mb-6">{props.subheading}</p>
                {props.ctaLabel && (
                  <Link to={props.ctaUrl || '#'} className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg inline-block">
                    {props.ctaLabel}
                  </Link>
                )}
              </div>
              <div className="bg-zinc-800 rounded-lg aspect-video flex items-center justify-center">
                {props.mediaUrl ? (
                  <img src={props.mediaUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <span className="text-zinc-500">Media</span>
                )}
              </div>
            </div>
          </div>
        </section>
      );
    
    // ========================================================================
    // Series Section Renderers
    // ========================================================================
    case 'section_series_hero':
      return <SeriesHeroSection block={block} isPreview={isPreview} />;
    
    case 'section_episode_list':
      return <EpisodeListSection block={block} isPreview={isPreview} />;
    
    case 'section_series_info':
      return <SeriesInfoPanelSection block={block} isPreview={isPreview} />;
    
    case 'section_series_comments':
      return <SeriesCommentsSection block={block} isPreview={isPreview} />;
    
    case 'section_related_series':
      return <RelatedSeriesSection block={block} isPreview={isPreview} />;
    
    case 'section_series_creator':
      return <SeriesCreatorSpotlightSection block={block} isPreview={isPreview} />;
    
    case 'section_series_cta':
      return <SeriesCTASection block={block} isPreview={isPreview} />;
    
    // Also support template IDs without the section_ prefix
    case 'series_hero':
      return <SeriesHeroSection block={block} isPreview={isPreview} />;
    
    case 'episode_list':
      return <EpisodeListSection block={block} isPreview={isPreview} />;
    
    case 'series_info_panel':
      return <SeriesInfoPanelSection block={block} isPreview={isPreview} />;
    
    case 'series_comments':
      return <SeriesCommentsSection block={block} isPreview={isPreview} />;
    
    case 'related_series':
      return <RelatedSeriesSection block={block} isPreview={isPreview} />;
    
    case 'series_creator_spotlight':
      return <SeriesCreatorSpotlightSection block={block} isPreview={isPreview} />;
    
    case 'series_cta':
      return <SeriesCTASection block={block} isPreview={isPreview} />;
    
    // Default fallback
    default:
      return (
        <section className="py-8" style={bgStyle}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <p className="text-zinc-500 text-sm text-center">Section: {type}</p>
          </div>
        </section>
      );
  }
};

/**
 * PageRenderer - Main canonical page renderer component
 */
const PageRenderer = ({ pageId: propsPageId }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAdmin = useIsAdmin();
  
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renderVersion, setRenderVersion] = useState('published');
  
  // Determine pageId from props or location
  const pageId = useMemo(() => {
    if (propsPageId) return propsPageId;
    
    const pathname = location.pathname;
    const routeMap = {
      '/': 'homepage',
      '/home': 'homepage',
      '/discover': 'discovery',
      '/comics': 'browse_comics',
      '/novels': 'browse_novels',
      '/gallery': 'gallery',
      '/leaderboard': 'leaderboard',
      '/early-access': 'landing_early_access',
      '/landing': 'landing',
      '/art-gallery': 'art_gallery',
      '/publishers': 'publishers_page',
      '/human-verified': 'human_verified',
    };
    
    return routeMap[pathname] || pathname.replace(/^\//, '').replace(/\//g, '_');
  }, [propsPageId, location.pathname]);
  
  // Check for admin preview mode
  useEffect(() => {
    const previewParam = searchParams.get('preview');
    if (isAdmin && previewParam === 'draft') {
      setRenderVersion('draft');
    } else {
      setRenderVersion('published');
    }
  }, [searchParams, isAdmin]);
  
  // Fetch page data
  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const version = isAdmin && renderVersion === 'draft' ? 'draft' : 'published';
        
        const response = await axios.get(
          `${API}/api/page-builder/render/${pageId}?version=${version}`,
          { headers }
        );
        
        setPageData(response.data);
        
        // Canonical verification log
        console.info(
          `%c[PageRenderer] Rendered by PageBuilderRenderer: pageId=${pageId}, status=${response.data.status || version}, blocks=${response.data.blocks?.length || 0}`,
          'color: #f97316; font-weight: bold;'
        );
        
      } catch (err) {
        console.error('[PageRenderer] Error fetching page:', err);
        setError(err.response?.data?.detail || err.message);
        
        // Admin fallback
        if (isAdmin) {
          try {
            const token = localStorage.getItem('token');
            const fallbackResponse = await axios.get(
              `${API}/api/admin/page-builder/pages/${pageId}?version=draft`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setPageData(fallbackResponse.data);
            setError(null);
          } catch (fallbackErr) {
            // Keep original error
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPage();
  }, [pageId, isAdmin, renderVersion]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }
  
  // Error state
  if (error && !pageData) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Page Load Error</h1>
          <p className="text-zinc-400 mb-4">{error}</p>
          <p className="text-zinc-600 text-sm">Page ID: {pageId}</p>
        </div>
      </div>
    );
  }
  
  // Render page
  const sortedBlocks = [...(pageData?.blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const enabledBlocks = sortedBlocks.filter(b => b.enabled !== false);
  
  return (
    <div 
      className="min-h-screen bg-zinc-950" 
      data-page-id={pageId}
      data-render-version={renderVersion}
      data-testid="page-builder-renderer"
    >
      {/* Admin Preview Banner */}
      {isAdmin && renderVersion === 'draft' && (
        <div className="sticky top-0 z-50 bg-yellow-500 text-black py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2">
          <Eye className="w-4 h-4" />
          DRAFT PREVIEW - Not visible to public users
          <Link to={location.pathname} className="ml-4 underline hover:no-underline">
            View Published
          </Link>
        </div>
      )}
      
      {/* Render sections with data fetching */}
      {enabledBlocks.map((block) => (
        <DataFetchingSection 
          key={block.id} 
          block={block}
          isPreview={renderVersion === 'draft'}
        />
      ))}
      
      {/* Empty state */}
      {enabledBlocks.length === 0 && (
        <div className="min-h-[50vh] flex items-center justify-center">
          <p className="text-zinc-500">No content sections defined for this page.</p>
        </div>
      )}
    </div>
  );
};

export default PageRenderer;
export { DataFetchingSection, ContentCard, CreatorCard, ArtworkCard };
